import { FormControl } from '@angular/forms';
import { TeamRequest } from '../views/team-shift/models/team-shift.model';

export interface Departure {
  created_at: number;
  id: string;
  is_deleted: boolean;
  number: number;
  object_address: string;
  object_name: string;
  object_number: number;
  report_status: ReportStatus;
  statuses: Array<{ status: DepartureStatus; created_at: number }>;
  team_name: string;
  report?: Report;
  awaitingCancelReport?: boolean;
  delivered_at: number | null;
  updated_at: number;
}

export interface DepartureWithControls {
  created_at: number;
  id: string;
  is_deleted: boolean;
  number: number;
  object_address: string;
  object_name: string;
  object_number: number;
  report: ReportWithControls;
  report_status: ReportStatus;
  status?: { status: DepartureStatus; created_at: number };
  statuses: Array<{ status: DepartureStatus; created_at: number }>;
  team_name: string;
  delivered_at: number | null;
  updated_at: number;
}

export interface RawDepartureResponse {
  departures: Array<Departure & { alarm_type: AlarmType; alarm_tags?: AlarmTag[] }>;
  pagination: {
    total_items: number;
    current_page: number;
    total_pages: number;
    limit: number;
    offset: number;
  };
}

export enum AlarmTag {
  SosButton = 'sos_button',
  SecurityAlarm = 'security_alarm',
  FireAlarm = 'fire_alarm',
  TechnicalReason = 'technical_reason',
  ClientError = 'client_error',
  NoAccess = 'no_access',
  Detention = 'detention',
  MaterialDamage = 'material_damage',
  Burglary = 'burglary',
  DeviceReset = 'device_reset',
  SetupHelp = 'setup_help',
}

export enum AlarmType {
  Real = 'real',
  Fake = 'fake',
  NoData = 'no_data',
  Canceled = 'canceled',
}

export enum DepartureStatus {
  New = 'new',
  EnRoute = 'en_route',
  OnObject = 'on_object',
  ReportFilled = 'report_filled',
  Canceled = 'canceled',
  Completed = 'completed',
  Closed = 'closed',
}

export enum ReportStatus {
  Declined = 'declined',
  Filled = 'filled',
  Approved = 'approved',
  Draft = 'draft',
}

export enum DepartureWSMessageType {
  Formed = 'DepartureFormed',
  DepartureClosed = 'DepartureClosed',
  DepartureAccepted = 'DepartureAccepted',
  TeamArrived = 'TeamArrived',
  DepartureCanceled = 'DepartureCanceled',
  ReceivedByTeam = 'DepartureReceivedByTeam',
  DepartureCompleted = 'DepartureCompleted',
}

export enum TeamStatus {
  TeamOnObject = 'on_object',
  TeamEnRoute = 'en_route',
  TeamAvailable = 'available',
}

export enum DepartureWSMethod {
  AcceptDeparture = 'departures:Accept',
  ArriveOnObject = 'departures:ArriveOnObject',
  Close = 'departures:Close',
  Cancel = 'departures:Cancel',
  GetDetails = 'departures:GetDetails',
  Delete = 'departures:MarkAsDeleted',
  History = 'departures:History',
  Complete = 'departures:Complete',
  ConfirmComplete = 'departures:ConfirmComplete',
  GetReport = 'departures:GetDepartureReport',
}

export interface DepartureDetails {
  departure_history: {
    status: DepartureStatus;
    created_at: number;
    initiator: string;
  }[];
  incident: { type: string; created_at: number } | null;
  team: string;
  object: {
    address: string;
    description: string;
    device_description: string;
    driveway_description: string;
    name: string;
    number: number;
    facade_photo?: string;
  };
  responsible_persons: {
    name: string;
    surname: string;
    patronomic: string;
    address: string;
    phone_numbers: null | string[];
  }[];
  crew_members?: {
    driver: string;
    guard?: string;
    intern?: string;
  };
}

export interface DepartureWSMessage {
  detail_type: DepartureWSMessageType;
  detail: Departure | { departure_id: string; timestamp: number; status: DepartureStatus };
}

export enum ReportWSMessageType {
  Approved = 'ReportApproved',
  Filled = 'ReportFilled',
  Declined = 'ReportDeclined',
  CancelSaved = 'CancelReportSaved',
}

export enum DrawerType {
  FillingReport = 'fillingReport',
  FillingDecliningComment = 'fillingDecliningComment',
  FillingApprovingComment = 'fillingApprovingComment',
  ViewingReport = 'viewingReport',
  ViewingInfo = 'viewingInfo',
  ViewingRoute = 'viewingRoute',
  FillingCancelingComment = 'fillingCancelingComment',
  FillingCancelReport = 'fillingCancelReport',
}

export interface FilterType {
  filters: FilterType[];
  is_selected: boolean;
  name: string;
  report_steps: CheckboxType[][];
}

export interface CancelReport {
  reason_code: number;
  other_reason?: string;
}

export interface Report {
  cancelation_reason: null | string;
  is_canceled?: boolean;
  cancel_report?: CancelReport | null;
  departure_id: string;
  dispatcher_report: {
    input_type: string;
    is_selected: boolean;
    name: string;
    value: null | string;
  }[];
  filled_at: number | null;
  dispatcher_commentary: string;
  filters: FilterType[];
  template_id: string;
  photo_report: {
    photo: null | string;
    photo_description: string;
    step_id: null;
    is_required: boolean;
    photo_commentary: null;
    photo_size?: number;
  }[];
  id: string;
  status: ReportStatus;
  max_bot_report?: string | null;
}

export interface ReportWithControls {
  cancelation_reason: null | string;
  departure_id: string;
  dispatcher_report: {
    input_type: string;
    is_selected: boolean;
    name: string;
    value: null | string;
  }[];
  filled_at: number | null;
  dispatcher_commentary: string;
  filters: FilterTypeWithControls[];
  template_id: string;
  photo_report: {
    photo: null | string;
    photo_description: string;
    step_id: null;
    is_required: boolean;
    photo_commentary: null;
  }[];
  id: string;
  status: ReportStatus;
  max_bot_report?: string | null;
}

export interface FilterTypeWithControls {
  filters: FilterTypeWithControls[];
  is_selected: FormControl<boolean>;
  name: string;
  report_steps: CheckboxTypeWithControls[][];
}

export interface CheckboxTypeWithControls {
  input_type: string;
  value: null | FormControl<string>;
  is_photo_required: boolean;
  is_selected: FormControl<boolean>;
  photo_description: string | null;
  name: string;
  showTextarea?: boolean;
}

export interface Team {
  id: string;
  name: string;
  status: string;
  object_number: number | null;
  shift?: TeamRequest;
  is_on_shift: boolean;
}

export interface CheckboxType {
  input_type: string;
  value: null | string;
  is_photo_required: boolean;
  is_selected: boolean;
  photo_description: string | null;
  name: string;
}

export interface DispatcherComment {
  name: string;
  is_selected: FormControl<boolean>;
  textControl?: FormControl<string>;
  showTextarea: boolean;
}

export enum TrackType {
  Raw = 'raw',
  Clean = 'clean',
  Matched = 'matched',
}

export interface Track {
  id: string;
  departure_id: string;
  points: {
    latitude: number;
    longitude: number;
  }[];
  kind: TrackType;
  created_at: number;
  updated_at: number;
}
